// components/PromptReferenceTagCards.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, Mic, Video } from '@/components/ui/lucide-memo';
import { LockableFile } from '#types/types';

type PromptReferenceKind = 'image' | 'video' | 'audio' | 'workspace';

type PromptReferenceToken = {
  kind: PromptReferenceKind;
  index: number;
  key: string;
};

type ImageReferenceItem = {
  id: string;
  label: string;
  file: File;
};

type PromptSegment =
  | { type: 'text'; key: string; value: string }
  | { type: 'token'; key: string; token: PromptReferenceToken };

interface PromptReferenceTagCardsProps {
  prompt: string;
  imageReferences: LockableFile[];
  onPromptChange: (nextPrompt: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  className?: string;
  tokensOnly?: boolean;
}

const TOKEN_REGEX = /@(workspace|image|video|audio)(\d+)?\b/gi;

const parsePromptReferenceTokens = (prompt: string): PromptReferenceToken[] => {
  const result: PromptReferenceToken[] = [];
  let match: RegExpExecArray | null = null;
  const source = typeof prompt === 'string' ? prompt : '';
  let tokenCounter = 0;
  TOKEN_REGEX.lastIndex = 0;
  while ((match = TOKEN_REGEX.exec(source)) !== null) {
    const rawKind = (match[1] ?? '').toLowerCase();
    const rawIndex = Number.parseInt(match[2] ?? '', 10);
    if (rawKind === 'workspace' && !match[2]) {
      const key = `workspace:0:${tokenCounter}`;
      tokenCounter += 1;
      result.push({ kind: 'workspace', index: 0, key });
      continue;
    }
    if (
      (rawKind !== 'image' && rawKind !== 'video' && rawKind !== 'audio') ||
      !Number.isFinite(rawIndex) ||
      rawIndex <= 0
    ) {
      continue;
    }
    const kind = rawKind as PromptReferenceKind;
    const key = `${kind}:${rawIndex}:${tokenCounter}`;
    tokenCounter += 1;
    result.push({ kind, index: rawIndex, key });
  }
  return result;
};

const splitPromptIntoSegments = (prompt: string): PromptSegment[] => {
  const source = typeof prompt === 'string' ? prompt : '';
  if (!source) return [{ type: 'text', key: 'text:empty', value: '' }];
  const segments: PromptSegment[] = [];
  TOKEN_REGEX.lastIndex = 0;
  let cursor = 0;
  let tokenCounter = 0;
  let match: RegExpExecArray | null = null;
  while ((match = TOKEN_REGEX.exec(source)) !== null) {
    const tokenText = match[0] ?? '';
    const start = match.index;
    const end = start + tokenText.length;
    if (start > cursor) {
      segments.push({
        type: 'text',
        key: `text:${cursor}:${start}`,
        value: source.slice(cursor, start),
      });
    }
    const rawKind = (match[1] ?? '').toLowerCase();
    const rawIndex = Number.parseInt(match[2] ?? '', 10);
    if (rawKind === 'workspace' && !match[2]) {
      const token: PromptReferenceToken = {
        kind: 'workspace',
        index: 0,
        key: `workspace:0:${tokenCounter}`,
      };
      segments.push({ type: 'token', key: `token:${token.key}`, token });
      tokenCounter += 1;
    } else if (
      (rawKind === 'image' || rawKind === 'video' || rawKind === 'audio') &&
      Number.isFinite(rawIndex) &&
      rawIndex > 0
    ) {
      const token: PromptReferenceToken = {
        kind: rawKind as PromptReferenceKind,
        index: rawIndex,
        key: `${rawKind}:${rawIndex}:${tokenCounter}`,
      };
      segments.push({ type: 'token', key: `token:${token.key}`, token });
      tokenCounter += 1;
    } else {
      segments.push({
        type: 'text',
        key: `text:${start}:${end}`,
        value: tokenText,
      });
    }
    cursor = end;
  }
  if (cursor < source.length) {
    segments.push({
      type: 'text',
      key: `text:${cursor}:${source.length}`,
      value: source.slice(cursor),
    });
  }
  return segments;
};

const replacePromptReferenceToken = (
  prompt: string,
  kind: PromptReferenceKind,
  fromIndex: number,
  toIndex: number,
): string => {
  if (fromIndex === toIndex) return prompt;
  const safeKind = kind.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = new RegExp(`@${safeKind}${fromIndex}\\b`, 'gi');
  return prompt.replace(pattern, `@${kind}${toIndex}`);
};

const useImageReferenceItems = (imageReferences: LockableFile[]): ImageReferenceItem[] => {
  const references = useMemo(() => (
    Array.isArray(imageReferences) ? imageReferences : []
  ), [imageReferences]);
  return useMemo(() => references.map((lockableFile, index) => ({
    id: lockableFile.id || `ref-${index + 1}`,
    label: lockableFile.file?.name || `Image ${index + 1}`,
    file: lockableFile.file,
  })), [references]);
};

const ImageReferencePreview = ({ item, style }: { item: ImageReferenceItem; style: React.CSSProperties }) => {
  const imageRef = useRef<HTMLImageElement>(null);
  const file = item.file;
  useEffect(() => {
    const url = URL.createObjectURL(file);
    if (imageRef.current) imageRef.current.src = url;
    return () => URL.revokeObjectURL(url);
  }, [file]);
  return <img ref={imageRef} alt={item.label} style={style} />;
};

const getTokenLabel = (kind: PromptReferenceKind, index: number): string => (
  kind === 'workspace' ? '@workspace' : `@${kind}${index}`
);

const getTokenIcon = (kind: PromptReferenceKind): React.ReactNode => {
  if (kind === 'video') return <Video size={12} strokeWidth={2} />;
  if (kind === 'audio') return <Mic size={12} strokeWidth={2} />;
  return <ImageIcon size={12} strokeWidth={2} />;
};

export const hasPromptReferenceTokens = (prompt: string): boolean => {
  TOKEN_REGEX.lastIndex = 0;
  return TOKEN_REGEX.test(prompt);
};

const TOKEN_BUTTON_BASE_STYLE: React.CSSProperties = {
  border: 'none',
  padding: '1px 6px',
  borderRadius: 6,
  lineHeight: '1.1rem',
  transform: 'translateY(-1px)',
};

const TOKEN_PREVIEW_STYLE: React.CSSProperties = {
  width: '14px',
  height: '14px',
  borderRadius: '3px',
  objectFit: 'cover',
  border: '1px solid rgba(255,255,255,0.25)',
  flexShrink: 0,
};

const REPLACEMENT_PREVIEW_STYLE: React.CSSProperties = {
  width: '20px',
  height: '20px',
  borderRadius: '4px',
  objectFit: 'cover',
  border: '1px solid rgba(255,255,255,0.25)',
  flexShrink: 0,
};

const MENU_BASE_STYLE: React.CSSProperties = {
  backdropFilter: 'blur(4px)',
  maxWidth: 'min(420px, calc(100% - 16px))',
};

interface PromptReferenceTokenButtonProps {
  token: PromptReferenceToken;
  imageItem: ImageReferenceItem | null;
  hasMatch: boolean;
  isExpanded: boolean;
  canSelect: boolean;
  onActivate: (token: PromptReferenceToken, elementRect: DOMRect) => void;
}

const PromptReferenceTokenButton: React.FC<PromptReferenceTokenButtonProps> = React.memo(({
  token,
  imageItem,
  hasMatch,
  isExpanded,
  canSelect,
  onActivate,
}) => {
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (!canSelect) return;
    onActivate(token, event.currentTarget.getBoundingClientRect());
  }, [canSelect, onActivate, token]);

  const style = useMemo<React.CSSProperties>(() => ({
    ...TOKEN_BUTTON_BASE_STYLE,
    backgroundColor: hasMatch
      ? 'rgba(179,217,255,0.18)'
      : 'rgba(127,29,29,0.24)',
    color: hasMatch ? '#f2f8ff' : '#fecaca',
    cursor: canSelect ? 'pointer' : 'default',
    boxShadow: isExpanded ? '0 0 0 1px rgba(179,217,255,0.45)' : 'none',
  }), [canSelect, hasMatch, isExpanded]);

  const isWorkspaceToken = token.kind === 'workspace';

  return (
    <button
      type="button"
      onClick={handleClick}
      className="pointer-events-auto inline-flex items-center gap-1 align-middle transition-colors"
      style={style}
      title={
        isWorkspaceToken
          ? 'Current workspace reference'
          : canSelect
            ? 'Click to change reference mapping'
            : 'Reference tag'
      }
    >
      {token.kind === 'image' && imageItem ? (
        <ImageReferencePreview
          item={imageItem}
          style={TOKEN_PREVIEW_STYLE}
        />
      ) : (
        <span className="inline-flex h-3.5 w-3.5 items-center justify-center opacity-85">
          {getTokenIcon(token.kind)}
        </span>
      )}
      <span className="font-medium">{getTokenLabel(token.kind, token.index)}</span>
    </button>
  );
});

PromptReferenceTokenButton.displayName = 'PromptReferenceTokenButton';

interface PromptReferenceReplacementButtonProps {
  token: PromptReferenceToken;
  item: ImageReferenceItem;
  replacementIndex: number;
  selected: boolean;
  onSelect: (token: PromptReferenceToken, replacementIndex: number) => void;
}

const PromptReferenceReplacementButton: React.FC<PromptReferenceReplacementButtonProps> = React.memo(({
  token,
  item,
  replacementIndex,
  selected,
  onSelect,
}) => {
  const handleClick = useCallback(() => {
    onSelect(token, replacementIndex);
  }, [onSelect, replacementIndex, token]);

  const style = useMemo<React.CSSProperties>(() => ({
    borderColor: selected ? 'rgba(179,217,255,0.7)' : 'rgba(255,255,255,0.2)',
    backgroundColor: selected ? 'rgba(179,217,255,0.2)' : 'rgba(255,255,255,0.03)',
    color: selected ? '#e8f4ff' : '#e5e7eb',
  }), [selected]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className="inline-flex items-center gap-1.5 rounded-md border px-1.5 py-1 text-[11px]"
      style={style}
    >
      <ImageReferencePreview
        item={item}
        style={REPLACEMENT_PREVIEW_STYLE}
      />
      <span className="font-medium">{getTokenLabel('image', replacementIndex)}</span>
    </button>
  );
});

PromptReferenceReplacementButton.displayName = 'PromptReferenceReplacementButton';

const PromptReferenceTagCards: React.FC<PromptReferenceTagCardsProps> = React.memo(({
  prompt,
  imageReferences,
  onPromptChange,
  textareaRef,
  className,
  tokensOnly = false,
}) => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const tokens = useMemo(() => parsePromptReferenceTokens(prompt), [prompt]);
  const segments = useMemo(() => splitPromptIntoSegments(prompt), [prompt]);
  const imageItems = useImageReferenceItems(imageReferences);
  const [expandedTokenKey, setExpandedTokenKey] = useState<string | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);
  const [isTextareaFocused, setIsTextareaFocused] = useState(false);

  if (expandedTokenKey && !tokens.some((token) => token.key === expandedTokenKey)) {
    setExpandedTokenKey(null);
    setMenuPosition(null);
  }

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const handleScroll = () => setScrollTop(textarea.scrollTop);
    const handleFocus = () => setIsTextareaFocused(true);
    const handleBlur = () => setIsTextareaFocused(false);
    setIsTextareaFocused(document.activeElement === textarea);
    handleScroll();
    textarea.addEventListener('scroll', handleScroll);
    textarea.addEventListener('focus', handleFocus);
    textarea.addEventListener('blur', handleBlur);
    return () => {
      textarea.removeEventListener('scroll', handleScroll);
      textarea.removeEventListener('focus', handleFocus);
      textarea.removeEventListener('blur', handleBlur);
    };
  }, [textareaRef]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!rootRef.current) return;
      const target = event.target as Node | null;
      if (target && rootRef.current.contains(target)) return;
      setExpandedTokenKey(null);
      setMenuPosition(null);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const handleSelectReplacement = useCallback((token: PromptReferenceToken, replacementIndex: number) => {
    if (replacementIndex <= 0) return;
    const nextPrompt = replacePromptReferenceToken(prompt, token.kind, token.index, replacementIndex);
    if (nextPrompt === prompt) return;
    onPromptChange(nextPrompt);
    setExpandedTokenKey(null);
    setMenuPosition(null);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [onPromptChange, prompt, textareaRef]);

  const handleTokenActivate = useCallback((token: PromptReferenceToken, elementRect: DOMRect) => {
    const rootRect = rootRef.current?.getBoundingClientRect();
    if (rootRect) {
      setMenuPosition({
        left: Math.max(8, elementRect.left - rootRect.left),
        top: elementRect.bottom - rootRect.top + 6,
      });
    }
    setExpandedTokenKey((current) => (current === token.key ? null : token.key));
  }, []);

  const scrollStyle = useMemo<React.CSSProperties>(() => ({
    transform: `translateY(${-scrollTop}px)`,
  }), [scrollTop]);

  const menuStyle = useMemo<React.CSSProperties>(() => ({
    ...MENU_BASE_STYLE,
    left: menuPosition?.left,
    top: menuPosition?.top,
  }), [menuPosition]);

  if (tokens.length === 0 || isTextareaFocused) return null;

  const activeToken = expandedTokenKey
    ? tokens.find((token) => token.key === expandedTokenKey) ?? null
    : null;

  return (
    <div
      ref={rootRef}
      className={className || 'pointer-events-none absolute inset-0'}
      aria-hidden={false}
    >
      <div className="h-full overflow-hidden">
        <div style={scrollStyle}>
          <div className={tokensOnly ? 'flex flex-wrap gap-1.5' : 'whitespace-pre-wrap break-words'}>
            {segments.map((segment) => {
              if (segment.type === 'text') {
                return tokensOnly ? null : <span key={segment.key}>{segment.value}</span>;
              }
              const token = segment.token;
              const isImageToken = token.kind === 'image';
              const imageItem = isImageToken ? imageItems[token.index - 1] : null;
              return (
                <PromptReferenceTokenButton
                  key={segment.key}
                  token={token}
                  imageItem={imageItem ?? null}
                  hasMatch={token.kind === 'workspace' || Boolean(imageItem)}
                  isExpanded={expandedTokenKey === token.key}
                  canSelect={isImageToken && imageItems.length > 0}
                  onActivate={handleTokenActivate}
                />
              );
            })}
          </div>
        </div>
      </div>

      {activeToken && activeToken.kind === 'image' && imageItems.length > 0 && menuPosition && (
        <div
          className="pointer-events-auto absolute z-20 rounded-[12px] border border-white/[0.06] bg-[#353535] p-2 shadow-2xl"
          style={menuStyle}
        >
          <div className="flex max-h-32 flex-wrap gap-1.5 overflow-y-auto">
            {imageItems.map((item, index) => {
              const replacementIndex = index + 1;
              return (
                <PromptReferenceReplacementButton
                  key={`${expandedTokenKey}:${item.id}:${replacementIndex}`}
                  token={activeToken}
                  item={item}
                  replacementIndex={replacementIndex}
                  selected={replacementIndex === activeToken.index}
                  onSelect={handleSelectReplacement}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});

PromptReferenceTagCards.displayName = 'PromptReferenceTagCards';

export default PromptReferenceTagCards;
